import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X, MapPin, Clock, Navigation, UserCog, Bell, ExternalLink, Briefcase, Route as RouteIcon, Play, CheckCircle, Users } from 'lucide-react';
import { FieldMap } from '../components/map/FieldMap';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Input';
import { JobForm } from '../components/jobs/JobForm';
import { useJobs } from '../hooks/useJobs';
import { useLocations } from '../hooks/useLocations';
import { updateJob, getJob, getActiveRoutes } from '../api/jobs';
import { getFieldStaff } from '../api/users';
import { StatusBadge, PriorityBadge } from '../components/jobs/StatusBadge';
import { STATUS_LABELS, PRIORITY_BORDER } from '../utils/constants';

const MAP_ACTIVE_STATUSES = ['pending', 'assigned', 'on_the_way', 'started'];

const QUICK_FILTERS = [
  { key: 'all',       label: 'Tümü',        match: s => MAP_ACTIVE_STATUSES.includes(s) },
  { key: 'pending',   label: 'Bekliyor',    match: s => s === 'pending' },
  { key: 'assigned',  label: 'Atandı',      match: s => s === 'assigned' },
  { key: 'on_the_way',label: 'Yolda',       match: s => s === 'on_the_way' },
  { key: 'started',   label: 'Devam',       match: s => s === 'started' },
];

export function MapPage() {
  const { jobs, loading, reload } = useJobs({ exclude_statuses: 'completed,approved,cancelled' });
  const { locations } = useLocations();
  const mapRef = useRef(null);

  const [search, setSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [reassignJob, setReassignJob] = useState(null);
  const [directionsRoutes, setDirectionsRoutes] = useState({});

  const onlineCount = Object.keys(locations).length;

  // Aktif rotaları yükle (on_the_way personeller)
  const loadRoutes = useCallback(async () => {
    try {
      const data = await getActiveRoutes();
      const routeMap = {};
      for (const r of data.routes) {
        if (!r.staff_lat || !r.staff_lng || !r.job_lat || !r.job_lng) continue;
        routeMap[r.job_id] = {
          origin: { lat: parseFloat(r.staff_lat), lng: parseFloat(r.staff_lng) },
          destination: { lat: parseFloat(r.job_lat), lng: parseFloat(r.job_lng) },
          staffName: r.staff_name,
          jobTitle: r.title,
        };
      }
      setDirectionsRoutes(routeMap);
    } catch {}
  }, []);

  useEffect(() => {
    loadRoutes();
    const interval = setInterval(loadRoutes, 30000); // 30 saniyede bir güncelle
    return () => clearInterval(interval);
  }, [loadRoutes]);

  // Filtreleme
  const filteredJobs = useMemo(() => {
    let list = jobs;
    const qf = QUICK_FILTERS.find(f => f.key === quickFilter);
    if (qf?.match) list = list.filter(j => qf.match(j.status));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(j =>
        j.title?.toLowerCase().includes(q) ||
        j.address?.toLowerCase().includes(q) ||
        j.assigned_to_name?.toLowerCase().includes(q) ||
        j.job_number?.toString().includes(q)
      );
    }
    return list;
  }, [jobs, quickFilter, search]);

  // İş seçimi + haritada odaklama
  const handleSelectJob = (job) => {
    setSelectedJob(job);
    const lat = parseFloat(job.latitude);
    const lng = parseFloat(job.longitude);
    if (isFinite(lat) && isFinite(lng)) {
      mapRef.current?.panTo(lat, lng, 16);
    }
  };

  // Google Maps navigasyon linki
  const getNavigationUrl = (job) => {
    const lat = parseFloat(job.latitude);
    const lng = parseFloat(job.longitude);
    if (!isFinite(lat) || !isFinite(lng)) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  };

  // Durum sayaçları (sadece haritada görünen aktif işler)
  const stats = useMemo(() => {
    const active = jobs.filter(j => MAP_ACTIVE_STATUSES.includes(j.status));
    const s = { total: active.length, pending: 0, assigned: 0, on_the_way: 0, started: 0 };
    active.forEach(j => { if (s[j.status] !== undefined) s[j.status]++; });
    return s;
  }, [jobs]);

  return (
    <div className="h-screen flex overflow-hidden">
      {/* ═══ SOL PANEL ═══ */}
      <div className="w-[380px] bg-white border-r flex flex-col shrink-0">
        {/* Arama + Filtreler */}
        <div className="p-3 border-b space-y-2.5 shrink-0">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="İş, adres veya personel ara..."
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex gap-1">
            {QUICK_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setQuickFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition
                  ${quickFilter === f.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* İş Listesi */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              <MapPin size={28} className="mx-auto mb-2 opacity-40" />
              Sonuç bulunamadı
            </div>
          ) : (
            filteredJobs.map(job => {
              const isSelected = selectedJob?.id === job.id;
              return (
                <button
                  key={job.id}
                  onClick={() => handleSelectJob(job)}
                  className={`w-full text-left px-3 py-3 border-b border-slate-100 transition
                    hover:bg-slate-50 border-l-[3px]
                    ${isSelected ? 'bg-blue-50 border-l-blue-500' : `${PRIORITY_BORDER[job.priority] || 'border-l-transparent'}`}`}
                >
                  <div className="flex items-start gap-2.5">
                    {/* Personel avatar */}
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 text-xs font-bold shrink-0 mt-0.5">
                      {job.assigned_to_name ? job.assigned_to_name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{job.title}</p>
                      </div>
                      {job.address && (
                        <p className="text-xs text-slate-500 truncate mt-0.5">{job.address}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <StatusBadge status={job.status} />
                        <PriorityBadge priority={job.priority} />
                      </div>
                      {job.assigned_to_name && (
                        <p className="text-xs text-slate-500 mt-1">
                          {job.assigned_to_name}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Alt Durum Barı */}
        <div className="border-t bg-slate-50 px-3 py-2.5 shrink-0">
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><Briefcase size={11} /> {stats.total} İş</span>
            <span className="flex items-center gap-1 text-slate-400"><Clock size={11} /> {stats.pending} Bekliyor</span>
            <span className="flex items-center gap-1 text-blue-500"><Bell size={11} /> {stats.assigned} Atandı</span>
            <span className="flex items-center gap-1 text-orange-500"><RouteIcon size={11} /> {stats.on_the_way} Yolda</span>
            <span className="flex items-center gap-1 text-yellow-600"><Play size={11} /> {stats.started} Devam</span>
            <span className="flex items-center gap-1 text-purple-600"><Users size={11} /> {onlineCount} Aktif</span>
          </div>
        </div>
      </div>

      {/* ═══ SAĞ ALAN (Harita + Detay Overlay) ═══ */}
      <div className="flex-1 relative">
        {/* Harita */}
        <FieldMap
          ref={mapRef}
          jobs={filteredJobs}
          locations={locations}
          onJobClick={handleSelectJob}
          selectedJobId={selectedJob?.id}
          directionsRoutes={directionsRoutes}
        />

        {/* Detay Overlay */}
        {selectedJob && (
          <DetailPanel
            job={selectedJob}
            locations={locations}
            onClose={() => setSelectedJob(null)}
            onEdit={(job) => { setEditJob(job); setSelectedJob(null); }}
            onReassign={(job) => setReassignJob(job)}
            onRefresh={async () => {
              reload();
              try {
                const d = await getJob(selectedJob.id);
                setSelectedJob(d.job);
              } catch { setSelectedJob(null); }
            }}
            navUrl={getNavigationUrl(selectedJob)}
          />
        )}
      </div>

      {/* Edit Modal */}
      <Modal open={!!editJob} onClose={() => setEditJob(null)} title="İşi Düzenle" size="lg">
        {editJob && (
          <JobForm
            job={editJob}
            onSuccess={() => { setEditJob(null); reload(); }}
            onCancel={() => setEditJob(null)}
          />
        )}
      </Modal>

      {/* Reassign Modal */}
      <Modal open={!!reassignJob} onClose={() => setReassignJob(null)} title="Personel Değiştir">
        {reassignJob && (
          <ReassignForm
            job={reassignJob}
            onSuccess={async () => {
              const jobId = reassignJob.id;
              setReassignJob(null);
              reload();
              try {
                const d = await getJob(jobId);
                setSelectedJob(d.job);
              } catch {}
            }}
            onCancel={() => setReassignJob(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// ═══════════════════════════
// Detay Paneli (Overlay)
// ═══════════════════════════
function DetailPanel({ job, locations, onClose, onEdit, onReassign, onRefresh, navUrl }) {
  // Personelin konumundan mesafe hesapla
  const staffLoc = job.assigned_to ? locations[job.assigned_to] : null;
  let distance = null;
  if (staffLoc && job.latitude && job.longitude) {
    const toRad = d => d * Math.PI / 180;
    const lat1 = toRad(parseFloat(staffLoc.latitude));
    const lat2 = toRad(parseFloat(job.latitude));
    const dLat = lat2 - lat1;
    const dLon = toRad(parseFloat(job.longitude) - parseFloat(staffLoc.longitude));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    distance = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  return (
    <div className="absolute top-3 right-3 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-10">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-slate-50">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-800 text-sm truncate">{job.title}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <StatusBadge status={job.status} />
              <PriorityBadge priority={job.priority} />
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-200 text-slate-400 shrink-0 ml-2">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* İçerik */}
      <div className="px-4 py-3 space-y-3 max-h-[50vh] overflow-y-auto">
        {/* Personel */}
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold shrink-0">
            {job.assigned_to_name ? job.assigned_to_name.charAt(0).toUpperCase() : '?'}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700">
              {job.assigned_to_name || 'Atanmamış'}
            </p>
            {distance !== null && (
              <p className="text-xs text-slate-400">{distance.toFixed(1)} km uzakta</p>
            )}
          </div>
        </div>

        {/* Adres */}
        {job.address && (
          <div className="flex items-start gap-2 text-xs text-slate-600">
            <MapPin size={13} className="text-slate-400 shrink-0 mt-0.5" />
            <span>{job.address}</span>
          </div>
        )}

        {/* Zaman bilgileri */}
        <div className="space-y-1 text-xs text-slate-500">
          {job.created_at && (
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-slate-400" />
              Oluşturulma: {new Date(job.created_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
          {job.scheduled_at && (
            <div className="flex items-center gap-2">
              <Clock size={12} className="text-blue-400" />
              Planlanan: {new Date(job.scheduled_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>

        {/* Açıklama */}
        {job.description && (
          <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">{job.description}</p>
        )}
      </div>

      {/* Aksiyonlar */}
      <div className="px-4 py-3 border-t bg-slate-50 flex flex-wrap gap-2">
        {navUrl && (
          <a href={navUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition">
            <Navigation size={12} /> Rotayı Göster
          </a>
        )}
        <Button variant="secondary" size="sm" onClick={() => onReassign(job)}>
          <UserCog size={12} /> Personel Değiştir
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onEdit(job)}>
          <ExternalLink size={12} /> Düzenle
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════
// Personel Değiştir Formu
// ═══════════════════════════
function ReassignForm({ job, onSuccess, onCancel }) {
  const [staffList, setStaffList] = useState([]);
  const [selected, setSelected] = useState(job.assigned_to || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFieldStaff().then(d => setStaffList(d.users)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateJob(job.id, { assigned_to: selected || undefined });
      onSuccess();
    } catch { }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-slate-600">
        <span className="font-medium">{job.title}</span> işi için yeni personel seçin:
      </p>
      <Select label="Personel" value={selected} onChange={e => setSelected(e.target.value)}>
        <option value="">— Atanmamış —</option>
        {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </Select>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" loading={loading}>Kaydet</Button>
      </div>
    </form>
  );
}
